import { Loader2 } from "lucide-react";
import { useToast } from "../ui/Toast";
import { useEffect, useState } from "react";
import api from "../../services/api";
import { trackEvent } from "../../utils/analytics";
import JournalFeed from "./JournalFeed";
import JournalInput from "./JournalInput";
import JournalSidebar from "./JournalSidebar";

const Journal = () => {
  const toast = useToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    trackEvent("journal_viewed");
    fetchJournalEntries();
  }, []);

  const fetchJournalEntries = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/journal");
      if (data.success) {
        // Sort by date DESC
        const sorted = (data.data.entries || []).sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        setEntries(sorted);
      }
    } catch (error) {
      console.error("Failed to fetch journal entries:", error);
      setError("Failed to load your journal.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntry = async (entryData) => {
    // If it's just a signal to refresh (e.g. from Daily Review), just fetch.
    if (entryData.entryType === "refresh_signal") {
        await fetchJournalEntries();
        return;
    }

    // UnifiedComposer already POSTs to /journal/log itself and hands us the
    // resulting entry. Re-fetch authoritatively so the feed always matches the
    // server (prepend-only could drop rapid successive logs).
    if (entryData.id) {
      await fetchJournalEntries();
      return;
    }

    try {
      const { data } = await api.post("/journal", entryData);

      if (data.success) {
        setEntries(prev => [data.data.entry, ...prev]);
      }
    } catch (error) {
      console.error("Failed to create entry:", error);
      toast.error("Failed to save entry. Please try again.");
    }
  };

  const handleUpdateEntry = async (id, updates) => {
    try {
      const { data } = await api.put(`/journal/${id}`, updates);
      if (data.success) {
        setEntries(prev =>
          prev.map(e => (e.id === id ? data.data.entry : e))
        );
        toast.success("Entry updated");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to update entry:", error);
      toast.error("Failed to update entry.");
      return false;
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      await api.delete(`/journal/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch (error) {
      console.error("Failed to delete entry:", error);
      toast.error("Failed to delete entry.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Loader2 className="animate-spin text-indigo-500" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-400 bg-slate-800 rounded-xl border border-red-500/20">
        <p>{error}</p>
        <button 
          onClick={fetchJournalEntries}
          className="mt-4 text-indigo-400 hover:text-indigo-300 underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 p-4 md:p-8 animate-fade-in">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Trading Journal</h1>
          <p className="text-slate-400 mt-1">Capture your setup, mindset, and execution in flow.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Main Feed Column (2/3) */}
        <div className="lg:col-span-2 space-y-8 order-2 lg:order-1">

          {/* Smart Input */}
          <JournalInput onSubmit={handleCreateEntry} />

          {/* Feed */}
          <JournalFeed
            entries={entries}
            onDelete={handleDeleteEntry}
            onUpdate={handleUpdateEntry}
          />

        </div>

        {/* Sidebar Column (1/3) — shown above feed on mobile */}
        <div className="space-y-6 order-1 lg:order-2">
          <JournalSidebar entries={entries} />
        </div>

      </div>

    </div>
  );
};

export default Journal;
